/**
 * Water Sample Form Modal
 * Acta de muestras de agua (Legionella) asociada a una orden de trabajo.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@/components/ui";
import {
  waterSamplesService,
  type WaterSampleAnalysisType,
  type WaterSampleContainerType,
  type WaterSampleContainerVolume,
  type WaterSampleControlType,
  type WaterSampleForm,
  type WaterSampleFormPayload,
  type WaterSampleLinePayload,
  type WaterSampleNature,
  type WaterSampleSamplingType,
  type WaterSampleSystemType,
} from "@/services/waterSamplesService";
import styles from "./WaterSampleFormModal.module.css";

interface WaterSampleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Cliente al que se asocia el acta (id del usuario en backend). */
  clientId: number | null;
  /** Nombre del cliente, para mostrar en cabecera. */
  clientName?: string;
  /** Dirección, para mostrar como referencia y prefilled del lugar de toma. */
  clientAddress?: string;
  /** Nº/identificador de la orden de trabajo asociada. */
  workOrderNumber?: string;
  /** Técnico que firma el acta (usuario actual). */
  technicianId?: number | null;
  /** Acta existente. Si se pasa, el modal se abre en modo edición/visualización. */
  existingForm?: WaterSampleForm | null;
  /** Disparado al guardar correctamente. */
  onSaved?: (form: WaterSampleForm) => void;
}

type ContainerTypeOption = {
  value: WaterSampleContainerType;
  label: string;
};

type AnalysisTypeOption = { value: WaterSampleAnalysisType; label: string };
type NatureOption = { value: WaterSampleNature; label: string };

const CONTAINER_TYPES: ContainerTypeOption[] = [
  { value: "plastico", label: "Plástico" },
  { value: "vidrio", label: "Vidrio" },
  { value: "bote_esteril_neutralizante", label: "Bote estéril neutralizante" },
  { value: "other", label: "Otros" },
];

const CONTAINER_VOLUMES: Array<{
  value: WaterSampleContainerVolume;
  label: string;
}> = [
  { value: "1l", label: "1 L" },
  { value: "0_5l", label: "0,5 L" },
  { value: "0_10l", label: "0,10 L" },
  { value: "other", label: "Otros" },
];

const ANALYSIS_TYPES: AnalysisTypeOption[] = [
  { value: "legionella", label: "Legionella" },
  { value: "aerobios_22c", label: "Aerobios 22ºC" },
  { value: "hierro", label: "Hierro" },
  { value: "other", label: "Otros" },
];

const SAMPLE_NATURES: NatureOption[] = [
  { value: "agua", label: "Agua" },
  { value: "biocapa", label: "Biocapa" },
  { value: "other", label: "Otros" },
];

const CONTROL_TYPES: Array<{ value: WaterSampleControlType; label: string }> = [
  { value: "routine", label: "Rutina" },
  { value: "post_cleaning", label: "Post limpieza" },
  { value: "outbreak", label: "Brote" },
];

const SYSTEM_TYPES: Array<{ value: WaterSampleSystemType; label: string }> = [
  { value: "acs", label: "ACS" },
  { value: "af", label: "AF" },
  { value: "cl", label: "CL" },
];

const SAMPLING_TYPES: Array<{
  value: WaterSampleSamplingType;
  label: string;
}> = [
  { value: "immersion", label: "Inmersión" },
  { value: "pre_flush", label: "Pre-flush" },
  { value: "post_flush", label: "Post-flush" },
];

const DEFAULT_TRANSPORTISTA =
  "MRW (los datos de entrega vienen recogidos en el albarán de entrega de MRW)";

interface SampleRow extends WaterSampleLinePayload {
  _key: string;
}

const buildEmptySample = (samplingPlace: string): SampleRow => ({
  _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  system_type: "acs",
  sampling_type: "immersion",
  fe_ppm: "",
  cl_ppm: "",
  temperature_c: "",
  ph: "",
  sample_time: "",
  sampling_place: samplingPlace,
});

const todayISO = () => new Date().toISOString().slice(0, 10);

const toNumberOrNull = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

export function WaterSampleFormModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  clientAddress,
  workOrderNumber,
  technicianId,
  existingForm,
  onSaved,
}: WaterSampleFormModalProps) {
  const isEditing = Boolean(existingForm?.id);

  const initialObservations = useMemo(
    () =>
      workOrderNumber
        ? `Acta asociada a la orden de trabajo ${workOrderNumber}.`
        : "",
    [workOrderNumber],
  );

  const [date, setDate] = useState<string>(todayISO());
  const [containerTypes, setContainerTypes] = useState<
    WaterSampleContainerType[]
  >([]);
  const [containerVolume, setContainerVolume] = useState<
    WaterSampleContainerVolume | ""
  >("");
  const [containerOther, setContainerOther] = useState<string>("");
  const [analysisTypes, setAnalysisTypes] = useState<WaterSampleAnalysisType[]>(
    ["legionella"],
  );
  const [analysisOther, setAnalysisOther] = useState<string>("");
  const [sampleNatures, setSampleNatures] = useState<WaterSampleNature[]>([
    "agua",
  ]);
  const [sampleNatureOther, setSampleNatureOther] = useState<string>("");
  const [controlType, setControlType] =
    useState<WaterSampleControlType>("routine");
  const [transportista, setTransportista] = useState<string>(
    DEFAULT_TRANSPORTISTA,
  );
  const [observations, setObservations] = useState<string>(initialObservations);
  const [samples, setSamples] = useState<SampleRow[]>(() => [
    buildEmptySample(clientAddress ?? ""),
  ]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Reset form whenever the modal is (re)opened.
  useEffect(() => {
    if (!isOpen) return;
    if (existingForm) {
      setDate(existingForm.date ?? todayISO());
      setContainerTypes(existingForm.container_types ?? []);
      setContainerVolume(
        (existingForm.container_volume as WaterSampleContainerVolume | "") ?? "",
      );
      setContainerOther(existingForm.container_other ?? "");
      setAnalysisTypes(existingForm.analysis_types ?? []);
      setAnalysisOther(existingForm.analysis_other ?? "");
      setSampleNatures(existingForm.sample_natures ?? []);
      setSampleNatureOther(existingForm.sample_nature_other ?? "");
      setControlType(
        (existingForm.control_type as WaterSampleControlType) ?? "routine",
      );
      setTransportista(existingForm.transportista ?? DEFAULT_TRANSPORTISTA);
      setObservations(existingForm.observations ?? "");
      const lines = existingForm.samples ?? [];
      setSamples(
        lines.length > 0
          ? lines.map((line, idx) => ({
              _key: `existing-${line.id ?? idx}`,
              system_type: line.system_type,
              sampling_type: line.sampling_type,
              fe_ppm: line.fe_ppm ?? "",
              cl_ppm: line.cl_ppm ?? "",
              temperature_c: line.temperature_c ?? "",
              ph: line.ph ?? "",
              sample_time: line.sample_time ?? "",
              sampling_place: line.sampling_place ?? "",
            }))
          : [buildEmptySample(clientAddress ?? "")],
      );
    } else {
      setDate(todayISO());
      setContainerTypes([]);
      setContainerVolume("");
      setContainerOther("");
      setAnalysisTypes(["legionella"]);
      setAnalysisOther("");
      setSampleNatures(["agua"]);
      setSampleNatureOther("");
      setControlType("routine");
      setTransportista(DEFAULT_TRANSPORTISTA);
      setObservations(initialObservations);
      setSamples([buildEmptySample(clientAddress ?? "")]);
    }
    setError(null);
    setIsSaving(false);
    setIsExporting(false);
  }, [isOpen, clientAddress, initialObservations, existingForm]);

  if (!isOpen) return null;

  const toggleValue = <T extends string>(
    list: T[],
    value: T,
    setter: (next: T[]) => void,
  ) => {
    setter(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    );
  };

  const updateSample = <K extends keyof WaterSampleLinePayload>(
    index: number,
    field: K,
    value: WaterSampleLinePayload[K],
  ) => {
    setSamples((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const addSampleRow = () => {
    setSamples((prev) => [...prev, buildEmptySample(clientAddress ?? "")]);
  };

  const removeSampleRow = (index: number) => {
    setSamples((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!clientId) {
      setError("Esta orden de trabajo no tiene un cliente válido asociado.");
      return;
    }
    if (!date) {
      setError("Indica la fecha del acta.");
      return;
    }
    if (containerTypes.length === 0) {
      setError("Selecciona al menos un tipo de envase.");
      return;
    }
    if (containerTypes.includes("other") && !containerOther.trim()) {
      setError('Indica el envase cuando seleccionas "Otros".');
      return;
    }
    if (analysisTypes.length === 0) {
      setError("Selecciona al menos un tipo de análisis.");
      return;
    }
    if (analysisTypes.includes("other") && !analysisOther.trim()) {
      setError('Indica el análisis cuando seleccionas "Otros".');
      return;
    }
    if (sampleNatures.length === 0) {
      setError("Selecciona al menos una naturaleza de muestra.");
      return;
    }
    if (sampleNatures.includes("other") && !sampleNatureOther.trim()) {
      setError('Indica la naturaleza cuando seleccionas "Otros".');
      return;
    }
    if (samples.length === 0) {
      setError("Añade al menos una muestra.");
      return;
    }

    const payload: WaterSampleFormPayload = {
      client: clientId,
      technician: technicianId ?? null,
      date,
      container_types: containerTypes,
      container_volume: containerVolume || undefined,
      container_other: containerTypes.includes("other")
        ? containerOther.trim()
        : null,
      analysis_types: analysisTypes,
      analysis_other: analysisTypes.includes("other")
        ? analysisOther.trim()
        : null,
      sample_natures: sampleNatures,
      sample_nature_other: sampleNatures.includes("other")
        ? sampleNatureOther.trim()
        : null,
      control_type: controlType,
      transportista: transportista.trim() || DEFAULT_TRANSPORTISTA,
      observations: observations.trim() || null,
      samples: samples.map((s) => ({
        system_type: s.system_type,
        sampling_type: s.sampling_type,
        fe_ppm: toNumberOrNull(s.fe_ppm),
        cl_ppm: toNumberOrNull(s.cl_ppm),
        temperature_c: toNumberOrNull(s.temperature_c),
        ph: toNumberOrNull(s.ph),
        sample_time: s.sample_time ? s.sample_time : null,
        sampling_place: s.sampling_place ?? "",
      })),
    };

    try {
      setIsSaving(true);
      const saved = existingForm?.id
        ? await waterSamplesService.update(existingForm.id, payload)
        : await waterSamplesService.create(payload);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Error al guardar el acta.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = async () => {
    if (!existingForm?.id) return;
    try {
      setIsExporting(true);
      setError(null);
      await waterSamplesService.exportPdf(existingForm.id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Error al exportar el acta.";
      setError(message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2>
              {isEditing
                ? "Acta de muestras de agua"
                : "Acta de muestras de agua"}
            </h2>
            <p>
              {clientName ? <strong>{clientName}</strong> : "Cliente"}
              {workOrderNumber ? ` · Orden ${workOrderNumber}` : ""}
              {existingForm?.id ? ` · Acta #${existingForm.id}` : ""}
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="xCircle" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.errorMessage}>{error}</div>}

          <section className={styles.section}>
            <h3>Datos generales</h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="ws-date">Fecha *</label>
                <input
                  id="ws-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="ws-control-type">Tipo de control *</label>
                <select
                  id="ws-control-type"
                  value={controlType}
                  onChange={(e) =>
                    setControlType(e.target.value as WaterSampleControlType)
                  }
                  required
                >
                  {CONTROL_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h3>Envase</h3>
            <div className={styles.formGrid}>
              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label>Tipo de envase *</label>
                <div className={styles.checkboxGroup}>
                  {CONTAINER_TYPES.map((opt) => {
                    const active = containerTypes.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`${styles.checkboxOption} ${
                          active ? styles.checkboxOptionActive : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() =>
                            toggleValue(
                              containerTypes,
                              opt.value,
                              setContainerTypes,
                            )
                          }
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="ws-volume">Volumen</label>
                <select
                  id="ws-volume"
                  value={containerVolume}
                  onChange={(e) =>
                    setContainerVolume(
                      e.target.value as WaterSampleContainerVolume | "",
                    )
                  }
                >
                  <option value="">Sin especificar</option>
                  {CONTAINER_VOLUMES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {containerTypes.includes("other") && (
                <div className={styles.formGroup}>
                  <label htmlFor="ws-container-other">Otro envase *</label>
                  <input
                    id="ws-container-other"
                    type="text"
                    value={containerOther}
                    onChange={(e) => setContainerOther(e.target.value)}
                    placeholder="Describe el envase"
                  />
                </div>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h3>Análisis y naturaleza</h3>
            <div className={styles.formGrid}>
              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label>Tipo de análisis *</label>
                <div className={styles.checkboxGroup}>
                  {ANALYSIS_TYPES.map((opt) => {
                    const active = analysisTypes.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`${styles.checkboxOption} ${
                          active ? styles.checkboxOptionActive : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() =>
                            toggleValue(
                              analysisTypes,
                              opt.value,
                              setAnalysisTypes,
                            )
                          }
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {analysisTypes.includes("other") && (
                <div className={styles.formGroup}>
                  <label htmlFor="ws-analysis-other">Otro análisis *</label>
                  <input
                    id="ws-analysis-other"
                    type="text"
                    value={analysisOther}
                    onChange={(e) => setAnalysisOther(e.target.value)}
                    placeholder="Describe el análisis"
                  />
                </div>
              )}

              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label>Naturaleza de la muestra *</label>
                <div className={styles.checkboxGroup}>
                  {SAMPLE_NATURES.map((opt) => {
                    const active = sampleNatures.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={`${styles.checkboxOption} ${
                          active ? styles.checkboxOptionActive : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() =>
                            toggleValue(
                              sampleNatures,
                              opt.value,
                              setSampleNatures,
                            )
                          }
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {sampleNatures.includes("other") && (
                <div className={styles.formGroup}>
                  <label htmlFor="ws-nature-other">Otra naturaleza *</label>
                  <input
                    id="ws-nature-other"
                    type="text"
                    value={sampleNatureOther}
                    onChange={(e) => setSampleNatureOther(e.target.value)}
                    placeholder="Describe la naturaleza"
                  />
                </div>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h3>Muestras</h3>
            <div className={styles.samplesTableWrapper}>
              <table className={styles.samplesTable}>
                <thead>
                  <tr>
                    <th>Sistema</th>
                    <th>Tipo de toma</th>
                    <th>Fe (ppm)</th>
                    <th>Cl (ppm)</th>
                    <th>Tª (ºC)</th>
                    <th>pH</th>
                    <th>Hora</th>
                    <th>Lugar de toma</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {samples.map((row, index) => (
                    <tr key={row._key}>
                      <td>
                        <select
                          value={row.system_type}
                          onChange={(e) =>
                            updateSample(
                              index,
                              "system_type",
                              e.target.value as WaterSampleSystemType,
                            )
                          }
                        >
                          {SYSTEM_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={row.sampling_type}
                          onChange={(e) =>
                            updateSample(
                              index,
                              "sampling_type",
                              e.target.value as WaterSampleSamplingType,
                            )
                          }
                        >
                          {SAMPLING_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={row.fe_ppm ?? ""}
                          onChange={(e) =>
                            updateSample(index, "fe_ppm", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={row.cl_ppm ?? ""}
                          onChange={(e) =>
                            updateSample(index, "cl_ppm", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={row.temperature_c ?? ""}
                          onChange={(e) =>
                            updateSample(index, "temperature_c", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="14"
                          value={row.ph ?? ""}
                          onChange={(e) =>
                            updateSample(index, "ph", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={row.sample_time ?? ""}
                          onChange={(e) =>
                            updateSample(index, "sample_time", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.sampling_place ?? ""}
                          onChange={(e) =>
                            updateSample(
                              index,
                              "sampling_place",
                              e.target.value,
                            )
                          }
                          placeholder="Lugar"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className={styles.removeRowButton}
                          onClick={() => removeSampleRow(index)}
                          disabled={samples.length === 1}
                          title="Eliminar muestra"
                          aria-label="Eliminar muestra"
                        >
                          <Icon name="xCircle" size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className={styles.addRowButton}
              onClick={addSampleRow}
            >
              <Icon name="plus" size={16} />
              Añadir muestra
            </button>
          </section>

          <section className={styles.section}>
            <h3>Transporte y observaciones</h3>
            <div className={styles.formGrid}>
              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label htmlFor="ws-transportista">Transportista</label>
                <input
                  id="ws-transportista"
                  type="text"
                  value={transportista}
                  onChange={(e) => setTransportista(e.target.value)}
                />
              </div>
              <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                <label htmlFor="ws-observations">Observaciones</label>
                <textarea
                  id="ws-observations"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Notas sobre la toma de muestras..."
                />
              </div>
            </div>
          </section>

          <div className={styles.modalActions}>
            {isEditing && (
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleExport}
                disabled={isExporting || isSaving}
                title="Descargar PDF del acta"
              >
                <Icon name="download" size={16} />
                {isExporting ? "Exportando..." : "Exportar PDF"}
              </button>
            )}
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={isSaving}
            >
              <Icon name="checkCircle" size={16} />
              {isSaving
                ? "Guardando..."
                : isEditing
                  ? "Guardar cambios"
                  : "Guardar acta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WaterSampleFormModal;
